import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, hotelsApi, setToken, clearToken, getToken } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverOnline, setServerOnline] = useState(true);

  useEffect(() => {
    // Restore session from sessionStorage
    const saved = sessionStorage.getItem('hms_session');
    const token = getToken();
    if (saved && token) {
      try {
        const session = JSON.parse(saved);
        setUser(session.user);
        setHotel(session.hotel);
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const result = await authApi.login(username, password);
      const { token, user: u, hotel: h } = result;

      // Store JWT
      setToken(token);

      // Store user (without password_hash) and hotel in session
      const safeUser = { ...u };
      delete safeUser.password_hash;

      setUser(safeUser);
      setHotel(h);
      setServerOnline(true);
      sessionStorage.setItem('hms_session', JSON.stringify({ user: safeUser, hotel: h }));
      return { user: safeUser, hotel: h };
    } catch (err) {
      if (err.message.includes('Cannot connect') || err.message.includes('NetworkError')) {
        setServerOnline(false);
        throw new Error('Cannot connect to server. Please start the API server and ensure XAMPP MySQL is running.');
      }
      throw err;
    }
  };

  const logout = () => {
    // Best-effort: clear the session server-side so the token can't keep working
    // (e.g. from a shared front-desk terminal) after the user thinks they've signed
    // out. Local state is cleared regardless of whether this call succeeds — the
    // user shouldn't be stuck "logged in" locally just because the API is offline.
    authApi.logout().catch(() => {});
    setUser(null);
    setHotel(null);
    clearToken();
    sessionStorage.removeItem('hms_session');
    sessionStorage.removeItem('hms_token');
  };

  const updateHotel = async (updatedHotel) => {
    setHotel(updatedHotel);
    const saved = sessionStorage.getItem('hms_session');
    if (saved) {
      const session = JSON.parse(saved);
      sessionStorage.setItem('hms_session', JSON.stringify({ ...session, hotel: updatedHotel }));
    }
  };

  // Refresh hotel list for superadmin
  const refreshHotels = async () => {
    try {
      return await hotelsApi.list();
    } catch { return []; }
  };

  const canAccessHotel = (hotelId) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    return user.hotel_id === hotelId;
  };

  const isAdmin = user?.role === 'superadmin' || user?.role === 'manager';

  return (
    <AuthContext.Provider value={{
      user, hotel, loading, serverOnline,
      login, logout, updateHotel, refreshHotels, canAccessHotel, isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
